import { useMemo } from 'react';
import { useMemoizedFn } from 'ahooks';
import { useTranslate } from '@tolgee/react';
import useMediaFile from '../../MediaPreview/useMediaFile';
import { useContextMenu } from '../ContextMenu';
import {
  useMsgAttachmentList,
  useUploadAttachment,
} from '../Toolbar/AttachmentUpload/hooks/useUploadAttachment';
import { validateAttachmentFile } from '../Toolbar/AttachmentUpload/utils';

/**
 * 消息编辑器媒体与粘贴
 * 单一职责：粘贴文件处理、右键菜单（复制/粘贴文本与文件）
 */
export function useMessageEditorMedia(
  conversation: Conversation.Conversation,
  insertText: (text: string) => void,
) {
  const { t } = useTranslate();
  const fileManager = useMediaFile(conversation);
  const uploadAttachment = useUploadAttachment();
  const attachmentList = useMsgAttachmentList(conversation.conversationId);

  const isJolect = conversation.channel === 'JOLECT';

  // Jolect 图片/文件走附件通道，粘贴时与工具栏附件上传保持同一套校验与上传逻辑
  const handlePasteFiles = useMemoizedFn((files: File[]) => {
    if (!isJolect) {
      fileManager(files);
      return;
    }

    const [file] = files;
    if (!file) {
      return;
    }
    if (!validateAttachmentFile(conversation.channel, file, attachmentList.length, t)) {
      return;
    }
    uploadAttachment(conversation.conversationId, conversation.storeId, conversation.region, file, {
      withMediaMetadata: true,
    });
  });

  const { showContextMenu, contextMenu } = useContextMenu(
    useMemo(
      () => ({
        onPasteText: insertText,
        onPasteFile: handlePasteFiles,
      }),
      [insertText, handlePasteFiles],
    ),
  );

  const handlePaste = useMemoizedFn((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (e?.clipboardData?.items?.length > 0) {
      const files = Array.from(e.clipboardData.items).reduce<File[]>((res, item) => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) res.push(file);
        }
        return res;
      }, []);
      if (files.length > 0) {
        e.preventDefault();
      }
      handlePasteFiles(files);
    }
  });

  const handleContextMenu = useMemoizedFn((e: React.MouseEvent) => {
    e.preventDefault();
    showContextMenu(e);
  });

  return {
    fileManager,
    handlePaste,
    showContextMenu,
    contextMenu,
    handleContextMenu,
  };
}
